import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CountriesService } from '@zero/onboarding/domain';
import { UserContactDataEmailComponent } from '@zero/self-service/feature-email-change/web';
import { TransactionAuthorizationComponent, UserMessagesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, IconComponent, SwitchComponent } from '@zero/shared/ui-web-design-system';
import { TinValidatorDirective } from '@zero/shared/util-form-validators';
import {
  AuthPayloadTicket,
  AuthorizationData,
  IsoCountriesRegex,
  Ticket,
  TicketData,
  TransactionAuthorizationResult,
} from '@zero/shared/util-models';
import { TicketFacade } from '@zero/tickets/domain';
import { Observable, map } from 'rxjs';
import { BaseComponent } from '../base/base.component';
import { UpdateIdentdocComponent } from '../update-identdoc/update-identdoc.component';

@Component({
  selector: 'self-service-user-contact-data',
  templateUrl: './user-contact-data.component.html',
  styleUrls: ['./user-contact-data.component.scss'],
  imports: [
    NgClass,
    FormsModule,
    TransactionAuthorizationComponent,
    UserMessagesComponent,
    UpdateIdentdocComponent,
    DatePipe,
    TinValidatorDirective,
    AsyncPipe,
    ButtonComponent,
    SwitchComponent,
    IconComponent,
    UserContactDataEmailComponent,
    NgbTooltipModule,
    TranslocoModule,
  ],
})
export class UserContactDataComponent extends BaseComponent implements OnInit {
  @ViewChild('f') public form: NgForm;

  public editData = {};
  public verificationMode = false;
  public hasOpenTicket$: Observable<boolean>;
  public showWeitere = false;
  public adAgree?: boolean;
  public dataToAuthorize: AuthorizationData<AuthPayloadTicket>;
  public updateIdentDocMode = false;
  public updateIdentDocResult: string;

  #ticket: Ticket;

  public readonly ATTR_NEW_STREET = TicketData.ATTR_NEW_STREET;
  public readonly ATTR_NEW_STREET2 = TicketData.ATTR_NEW_STREET2;
  public readonly ATTR_NEW_STREETNO = TicketData.ATTR_NEW_STREETNO;
  public readonly ATTR_NEW_STREETNO2 = TicketData.ATTR_NEW_STREETNO2;
  public readonly ATTR_NEW_POSTALCODE = TicketData.ATTR_NEW_POSTALCODE;
  public readonly ATTR_NEW_CITY = TicketData.ATTR_NEW_CITY;
  public readonly ATTR_NEW_COUNTRY = TicketData.ATTR_NEW_COUNTRY;
  public readonly ATTR_NEW_TAX_COUNTRY_1 = TicketData.ATTR_NEW_TAX_COUNTRY_1;
  public readonly ATTR_NEW_TAX_COUNTRY_2 = TicketData.ATTR_NEW_TAX_COUNTRY_2;
  public readonly ATTR_NEW_TAX_COUNTRY_3 = TicketData.ATTR_NEW_TAX_COUNTRY_3;
  public readonly ATTR_NEW_TAX_ID_1 = TicketData.ATTR_NEW_TAX_ID_1;
  public readonly ATTR_NEW_TAX_ID_2 = TicketData.ATTR_NEW_TAX_ID_2;
  public readonly ATTR_NEW_TAX_ID_3 = TicketData.ATTR_NEW_TAX_ID_3;

  public readonly countriesService = inject(CountriesService);
  readonly #ticketFacade = inject(TicketFacade);

  public ngOnInit(): void {
    this.#ticketFacade.loadTickets(true, 0, 99, 'OPEN');

    this.hasOpenTicket$ = this.#ticketFacade.tickets$.pipe(
      map((tickets) => tickets && tickets.filter((ticket) => ticket && ticket.type === 'ADDRESS' && ticket.status === 'OPEN').length > 0),
    );
    this.customerStateFacade.customerBaseData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((cbd) => {
      this.adAgree = cbd && cbd.adAgree === true;
    });
  }

  public edit(): void {
    this.editMode = true;
  }

  public onSubmit(form: NgForm): void {
    if (!this.verificationMode && form.valid) {
      this.startChangeAddress();
    }
  }

  private startChangeAddress(): void {
    this.#ticket = new Ticket();
    this.#ticket.customerId = this.currentCustomer.custId;
    this.#ticket.type = 'ADDRESS';

    this.#ticket.ticketData = new TicketData();
    if (this.editData[TicketData.ATTR_NEW_TAX_COUNTRY_1] === 'none') {
      this.editData[TicketData.ATTR_NEW_TAX_COUNTRY_1] = '';
    }
    this.#ticket.ticketData.additionalProperties = this.editData;
    this.dataToAuthorize = new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(this.#ticket));
    this.verificationMode = true;
  }

  public postCodeValidationPattern(): RegExp {
    if (IsoCountriesRegex.POSTCODES[this.editData[TicketData.ATTR_NEW_COUNTRY]]) {
      return IsoCountriesRegex.POSTCODES[this.editData[TicketData.ATTR_NEW_COUNTRY]];
    } else {
      return /.*/;
    }
  }

  public deleteRow(rowIdx: number): void {
    if (rowIdx === 2) {
      this.editData[TicketData.ATTR_NEW_TAX_COUNTRY_2] = this.editData[TicketData.ATTR_NEW_TAX_COUNTRY_3];
      this.editData[TicketData.ATTR_NEW_TAX_ID_2] = this.editData[TicketData.ATTR_NEW_TAX_ID_3];
    }
    this.editData[TicketData.ATTR_NEW_TAX_ID_3] = null;
    this.editData[TicketData.ATTR_NEW_TAX_COUNTRY_3] = null;
    if (!this.editData[TicketData.ATTR_NEW_TAX_COUNTRY_2]) {
      this.showWeitere = false;
    }
  }

  public changeAdAgree(): void {
    this.dataService.toggleAdAgree().subscribe((res) => {
      this.adAgree = res === true;
    });
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult): void {
    if (result.success) {
      this.messageService.clearAllMessages();
      this.messageService.addInfoMessage(
        'Die Autorisierung war erfolgreich. Deine Adresse wird in den nächsten Tagen geändert, ein' +
          ' Service-Ticket wurde dazu erstellt.',
        true,
      );
      this.dataToAuthorize = undefined;
      this.editMode = false;
      this.verificationMode = false;
      this.customerStateFacade.loadCustomerBaseData();
      this.#ticketFacade.loadTickets(true);
    } else {
      if (result.reason === 'CanceledByUser') {
        this.editMode = false;
        this.verificationMode = false;
        this.dataToAuthorize = undefined;
      }
    }
  }

  public startUpdateIdentDoc() {
    this.updateIdentDocMode = !this.updateIdentDocMode;
    this.updateIdentDocResult = undefined;
  }

  public updateIdentDocFinished(result: string) {
    this.updateIdentDocMode = false;
    this.updateIdentDocResult = result;
    this.customerStateFacade.loadCustomerBaseData();
  }
}
